<script setup lang="ts">import { toRef as _toRef, computed as _computed } from 'vue';

import { myAccountGiftLists, myAccountWishlists } from '@consumer/api'
import MyAccountApi from '@consumer/api/MyAccountApi'
import { useSharedData } from '@consumer/composables/sharedData'
import type { GiftList, GiftListItem } from '@consumer/types/serializers'
import { useFavoritesStore } from '@consumer/services/favorites/store'
import { pluralize } from '@shared/string'
import { useFlyOut } from '../FlyOut/composables'

withDefaults(defineProps<{ favorite: GiftListItem }>(), {  })

const __$temp_1 = (useSharedData()),
  currentUser = _toRef(__$temp_1, 'currentUser');
const __$temp_2 = (useFlyOut()),
  setFlyOutOpen = _toRef(__$temp_2, 'setOpen');
const favorites = useFavoritesStore()

// Using [undefined] here for the guest use case in which we assume the favorites list is always present.
// The "undefined" represents the Favorites list yet to be defined.
const giftLists = _computed(() => __props.favorite.giftLists.length ? __props.favorite.giftLists.slice(0, 3) : [undefined])

const addToListLabel = _computed(() => {
  const totalGiftLists = __props.favorite.giftLists.length
  if (totalGiftLists > giftLists.value.length) {
    const othersCount = totalGiftLists - giftLists.value.length
    return `+${othersCount} ${pluralize(othersCount, 'other')}`
  }
  else {
    return 'Add to list'
  }
})

function listPathFor (giftList: GiftList | undefined) {
  if (!giftList || giftList.listType === 'favorites') return MyAccountApi.favorites.path()
  if (giftList.contactName) return myAccountGiftLists.show.path(giftList)
  return myAccountWishlists.show.path(giftList)
}
</script>

<template>
  <GLink
    :href="favorite.listable.url"
    :name="favorite.listable.tagline"
    class="
      favorite
      flex items-center gap-2
      transition-background duration-300
      hover:bg-surface-blue
      rounded-md
      p-2
      w-full
    "
    @click.stop
  >
    <div class="relative mr-1">
      <img
        v-if="favorite.listable.imageUrl"
        :src="favorite.listable.imageUrl"
        class="absolute top-1 right-1 w-4 h-4 z-5 rounded-full border border-white"
        alt="Gift image"
      >
      <GiftlyImage :gift="favorite.listable" class="!<lg:w-17 !w-17"/>
    </div>
    <slot>
      <span class="flex items-center gap-1.5">
        <span>
          Added to
          <span v-for="addedList, i in giftLists" :key="addedList?.id || 'favorites-list'">
            <GLink :href="listPathFor(addedList)" class="link font-semibold favorite-list">
              {{ addedList?.name || "Favorites" }}
            </GLink>
            <span v-if="i < giftLists.length - 1" class="text-grey-400 align-middle h-1" style="font-size: 24px;">
              •
            </span>
          </span>
          <span v-if="currentUser">
            <span class="text-grey-400 align-middle" style="font-size: 24px;">
              •
            </span>
            <GPromiseButton
              primary
              flat
              size="small"
              :label="addToListLabel"
              @click.prevent.stop="favorites.openAddToListModal(favorite, {
                onOpen: () => {
                  setFlyOutOpen(false)
                }
              })"
            />
          </span>
        </span>
      </span>
    </slot>
  </GLink>
</template>
